import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, ArrowBack, ExpandMore, ExpandLess, GetApp } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';

const PRIMARY = '#263078';

const fmt2Date = (d) => {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};
const fmtDate = (s) => {
  if (!s) return '-';
  const d = new Date(s);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};
const fmtN  = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const fmtAmt = (n) => `₹${(parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const iconBtn = {
  color: 'rgba(255,255,255,0.85)',
  '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.15)' },
  borderRadius: '5px', p: '3px',
};

const ss = (status) => {
  if (!status) return { bg: '#f1f5f9', color: '#64748b' };
  const s = status.toLowerCase();
  if (s === 'achieved' || s === 'completed') return { bg: '#dcfce7', color: '#16a34a' };
  if (s === 'pending')                        return { bg: '#fef9c3', color: '#ca8a04' };
  if (s === 'overdue'  || s === 'expired')    return { bg: '#fee2e2', color: '#dc2626' };
  return { bg: '#eff6ff', color: '#1d4ed8' };
};

const groupByScheme = (data) => {
  const map = new Map();
  data.forEach((row) => {
    if (!map.has(row.SchemeId)) {
      map.set(row.SchemeId, {
        SchemeId: row.SchemeId, SchemeNumber: row.SchemeNumber,
        SupplierName: row.SupplierName,
        SchemeStartDate: row.SchemeStartDate, SchemeEndDate: row.SchemeEndDate,
        items: [],
      });
    }
    map.get(row.SchemeId).items.push(row);
  });
  return Array.from(map.values());
};

/* ── Compact Scheme Card ── */
const SchemeCard = ({ scheme }) => {
  const [open, setOpen] = useState(true);

  const totSoldQty = scheme.items.reduce((s, i) => s + (parseFloat(i.SoldQty)    || 0), 0);
  const totSoldAmt = scheme.items.reduce((s, i) => s + (parseFloat(i.SoldAmount) || 0), 0);
  const totBal     = scheme.items.reduce((s, i) => s + (parseFloat(i.Balance)    || 0), 0);

  const allDone = scheme.items.every(i => { const v = (i.Status||'').toLowerCase(); return v === 'achieved'||v === 'completed'; });
  const overallSt = allDone ? 'Achieved' : 'In Progress';
  const ost = ss(overallSt);

  /* header cols: #, Item, Type, TgtQty, TgtAmt, SoldQty, SoldAmt, Balance, Status */
  const TH = ['#','Item','Type','Tgt Qty','Tgt Amt','Sold Qty','Sold Amt','Balance','Status','Description'];
  const align = [,'left','left','right','right','right','right','right','left','left'];

  return (
    <div style={{ background:'#fff', borderRadius:6, border:'1px solid #e2e8f0', overflow:'hidden', marginBottom:6 }}>

      {/* ── Header row ── */}
      <div onClick={() => setOpen(o => !o)} style={{
        display:'flex', alignItems:'center', justifyContent:'space-between',
        padding:'5px 10px', cursor:'pointer',
        background:'linear-gradient(135deg,#1e3a5f 0%,#263078 100%)', userSelect:'none',
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, minWidth:0, flex:1, overflow: open ? 'hidden' : 'visible' }}>
          <span style={{
            minWidth:24, height:24, borderRadius:5, background:'rgba(255,255,255,0.15)',
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:11, fontWeight:800, color:'#fff', flexShrink:0,
          }}>#{scheme.SchemeNumber}</span>
          <div style={{ minWidth:0, flex:1 }}>
            <div style={{ fontSize:12, fontWeight:700, color:'#fff', whiteSpace: open ? 'nowrap' : 'normal', overflow: open ? 'hidden' : 'visible', textOverflow: open ? 'ellipsis' : 'unset' }}>
              {scheme.SupplierName || 'Unknown'}
            </div>
            <div style={{ fontSize:9, color:'rgba(255,255,255,0.6)', marginTop:1 }}>
              {fmtDate(scheme.SchemeStartDate)} → {fmtDate(scheme.SchemeEndDate)}
              &nbsp;·&nbsp;{scheme.items.length} item{scheme.items.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6, flexShrink:0 }}>
          <span style={{ padding:'1px 8px', borderRadius:8, fontSize:9, fontWeight:700, background:ost.bg, color:ost.color, whiteSpace:'nowrap' }}>
            {overallSt}
          </span>
          {open
            ? <ExpandLess style={{ fontSize:15, color:'rgba(255,255,255,0.7)' }} />
            : <ExpandMore style={{ fontSize:15, color:'rgba(255,255,255,0.7)' }} />}
        </div>
      </div>

      {/* ── KPI micro-strip ── */}
      <div style={{ display:'flex', background:'#f8fafc', borderBottom:'1px solid #f1f5f9' }}>
        {[
          { label:'Sold Qty',  value: fmtN(totSoldQty),  color:'#1d4ed8' },
          { label:'Sold Amt',  value: fmtAmt(totSoldAmt), color:'#059669' },
          { label:'Balance',   value: fmtN(totBal),       color: totBal > 0 ? '#d97706' : '#059669' },
        ].map(k => (
          <div key={k.label} style={{ flex:1, padding:'4px 8px', borderRight:'1px solid #f1f5f9', textAlign:'center' }}>
            <div style={{ fontSize:11, fontWeight:800, color:k.color, lineHeight:1.3 }}>{k.value}</div>
            <div style={{ fontSize:8, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.3px' }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* ── Items table ── */}
      {open && (
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', minWidth:520 }}>
            <thead>
              <tr style={{ background:'#f1f5f9' }}>
                {TH.map((h, i) => (
                  <th key={i} style={{
                    padding:'4px 8px', fontSize:9, fontWeight:700, color:'#64748b',
                    textTransform:'uppercase', letterSpacing:'0.3px', whiteSpace:'nowrap',
                    textAlign: align[i] || 'left', borderBottom:'1px solid #e2e8f0',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {scheme.items.map((item, idx) => {
                const st       = ss(item.Status);
                const isQtyTgt = item.IsTarget === 1;
                const balNeg   = (parseFloat(item.Balance) || 0) < 0;
                return (
                  <tr key={item.SchemeDetailId || idx}
                    style={{ borderBottom:'1px solid #f1f5f9', background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ padding:'4px 8px', fontSize:10, color:'#94a3b8', width:22 }}>{idx+1}</td>
                    <td style={{ padding:'4px 8px', fontSize:11, fontWeight:600, color:'#1e293b', maxWidth:150, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {item.ItemName || '-'}
                    </td>
                    <td style={{ padding:'4px 8px', fontSize:10 }}>
                      {item.IsTarget === null ? <span style={{ color:'#94a3b8' }}>-</span>
                        : isQtyTgt
                          ? <span style={{ color:'#7c3aed', fontWeight:600 }}>Qty</span>
                          : <span style={{ color:'#0891b2', fontWeight:600 }}>Amt</span>}
                    </td>
                    <td style={{ padding:'4px 8px', fontSize:11, fontWeight:600, color:'#1d4ed8', textAlign:'right' }}>
                      {item.TargetQty != null ? fmtN(item.TargetQty) : '-'}
                    </td>
                    <td style={{ padding:'4px 8px', fontSize:11, fontWeight:600, color:'#1d4ed8', textAlign:'right' }}>
                      {item.TargetAmount != null ? fmtAmt(item.TargetAmount) : '-'}
                    </td>
                    <td style={{ padding:'4px 8px', fontSize:11, fontWeight:700, color:'#059669', textAlign:'right' }}>{fmtN(item.SoldQty)}</td>
                    <td style={{ padding:'4px 8px', fontSize:11, fontWeight:700, color:'#059669', textAlign:'right' }}>{fmtAmt(item.SoldAmount)}</td>
                    <td style={{ padding:'4px 8px', fontSize:11, fontWeight:700, textAlign:'right', color: balNeg ? '#dc2626' : '#d97706' }}>
                      {fmtN(item.Balance)}
                    </td>
                    <td style={{ padding:'4px 8px' }}>
                      <span style={{ padding:'1px 7px', borderRadius:6, fontSize:9, fontWeight:700, background:st.bg, color:st.color, whiteSpace:'nowrap' }}>
                        {item.Status || '-'}
                      </span>
                    </td>

                    <td style={{ padding:'4px 8px' }}>
                      <span style={{ padding:'1px 7px', borderRadius:6, fontSize:9, fontWeight:700, background:st.bg, color:st.color, whiteSpace:'nowrap' }}>
                        {item.Description || '-'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ════════════════════════════ MAIN ════════════════════════════ */
const SchemeSummaryReport = () => {
  useSessionValidation();
  const { companyId, branchId, token, companyName, username } = useAuth();
  const { logAudit } = useAuditLog();
  const history = useHistory();

  const today = new Date();
  const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [toDate,   setToDate]   = useState(fmt2Date(today));
  const [rawData,  setRawData]  = useState([]);
  const [schemes,  setSchemes]  = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [fetched,  setFetched]  = useState(false);

  const fetchReport = async () => {
    if (!fromDate || !toDate) { setError('Please select date range'); return; }
    if (new Date(fromDate) > new Date(toDate)) { setError('From Date > To Date'); return; }
    if (!companyId || !branchId || !token) { setError('Auth data missing'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetchData('SchemeApi', `/SchemeSummary/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      const data = res?.response_data ?? (Array.isArray(res) ? res : res?.data ?? []);
      const arr  = Array.isArray(data) ? data : [];
      setRawData(arr);
      setSchemes(groupByScheme(arr));
      setFetched(true);
    } catch (err) {
      setError(`Error: ${err?.response?.status ? `HTTP ${err.response.status}` : err?.message || 'Unknown'}`);
      setFetched(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId && branchId && token) fetchReport();
  }, [companyId, branchId, token]); // eslint-disable-line

  const totalSchemes  = schemes.length;
  const totalItems    = rawData.length;
  const totalSoldAmt  = rawData.reduce((s, i) => s + (parseFloat(i.SoldAmount) || 0), 0);
  const achievedCount = schemes.filter(s =>
    s.items.every(i => { const v=(i.Status||'').toLowerCase(); return v==='achieved'||v==='completed'; })
  ).length;

  const handleExport = async () => {
    logAudit(AUDIT_ACTION.EXPORT, 'SchemeSummaryReport', 0);
    if (!schemes.length) return;

    const sectionHeaderLabels = [];
    const rawRows = [];

    // Summary header
    rawRows.push(['SCHEME SUMMARY']);
    rawRows.push([`Total Schemes: ${totalSchemes}`, `Total Items: ${totalItems}`, `Total Sold: ${fmtAmt(totalSoldAmt)}`, `Achieved: ${achievedCount}/${totalSchemes}`]);
    rawRows.push([]);

    // Column headers row
    rawRows.push(['#', 'Scheme No', 'Supplier', 'Start Date', 'End Date', 'Item', 'Target Type', 'Target Qty', 'Target Amt', 'Sold Qty', 'Sold Amt', 'Balance', 'Status','Description']);

    schemes.forEach((scheme, si) => {
      // Scheme section label
      const schemeLabel = `#${scheme.SchemeNumber} — ${scheme.SupplierName || 'Unknown'} (${fmtDate(scheme.SchemeStartDate)} → ${fmtDate(scheme.SchemeEndDate)})`;
      sectionHeaderLabels.push(schemeLabel);
      rawRows.push([schemeLabel]);

      scheme.items.forEach((item, idx) => {
        const targetType = item.IsTarget === null ? '-' : item.IsTarget === 1 ? 'Qty Target' : 'Amt Target';
        rawRows.push([
          idx + 1,
          scheme.SchemeNumber,
          scheme.SupplierName || '',
          fmtDate(scheme.SchemeStartDate),
          fmtDate(scheme.SchemeEndDate),
          item.ItemName || '',
          targetType,
          item.TargetQty != null ? parseFloat(item.TargetQty) || 0 : '-',
          item.TargetAmount != null ? parseFloat(item.TargetAmount) || 0 : '-',
          parseFloat(item.SoldQty) || 0,
          parseFloat(item.SoldAmount) || 0,
          parseFloat(item.Balance) || 0,
          item.Status || '-',
          item.Description || '-',
        ]);
      });

      // Scheme subtotal
      const totSoldQty = scheme.items.reduce((s, i) => s + (parseFloat(i.SoldQty) || 0), 0);
      const totSoldAmt = scheme.items.reduce((s, i) => s + (parseFloat(i.SoldAmount) || 0), 0);
      const totBal     = scheme.items.reduce((s, i) => s + (parseFloat(i.Balance) || 0), 0);
      rawRows.push(['', '', '', '', '', 'Sub Total', '', '', '', totSoldQty, totSoldAmt, totBal, '']);
      rawRows.push([]);
    });

    await exportStyledExcel({
      reportTitle:   'SCHEME SUMMARY REPORT',
      sheetName:     'Scheme Summary',
      fileName:      `SchemeSummary_${fromDate}_${toDate}.xlsx`,
      companyName,
      username,
      fromDate,
      toDate,
      rawRows,
      sectionHeaders: ['SCHEME SUMMARY', ...sectionHeaderLabels],
      columnWidths:  [4, 10, 22, 11, 11, 22, 11, 11, 12, 10, 12, 10, 11],
    });
  };

  return (
    <div style={{ minHeight:'100%', background:'#f0f2f5', padding:6, fontFamily:"'Inter',sans-serif", boxSizing:'border-box' }}>
      <LogoLoader open={loading} message="Loading..." />
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical:'top', horizontal:'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width:'100%' }}>{error}</Alert>
      </Snackbar>

      <div style={{ background:'#fff', borderRadius:8, boxShadow:'0 2px 8px rgba(0,0,0,0.07)', border:'1px solid #e2e8f0', overflow:'hidden' }}>

        {/* ── Toolbar ── */}
        <div style={{
          display:'flex', alignItems:'center', padding:'3px 8px',
          background:`linear-gradient(135deg,${PRIMARY} 0%,#1a237e 100%)`,
          gap:5, flexWrap:'nowrap', overflowX:'auto', overflowY:'hidden', scrollbarWidth:'none',
        }}>
          <Tooltip title="Back" arrow>
            <IconButton onClick={() => history.goBack()} size="small" sx={iconBtn}>
              <ArrowBack sx={{ fontSize:15 }} />
            </IconButton>
          </Tooltip>
          <span style={{ fontSize:12, fontWeight:700, color:'#fff', whiteSpace:'nowrap' }}>Scheme Summary Report</span>
          <div style={{ flex:1 }} />
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
            style={{ height:26, border:'1px solid rgba(255,255,255,0.3)', borderRadius:6, padding:'0 6px', fontSize:11, backgroundColor:'rgba(255,255,255,0.1)', color:'#fff', colorScheme:'dark', flexShrink:0 }} />
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
            style={{ height:26, border:'1px solid rgba(255,255,255,0.3)', borderRadius:6, padding:'0 6px', fontSize:11, backgroundColor:'rgba(255,255,255,0.1)', color:'#fff', colorScheme:'dark', flexShrink:0 }} />
          <Tooltip title="Refresh" arrow>
            <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
              <Refresh sx={{ fontSize:15 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Excel" arrow>
            <IconButton onClick={handleExport} disabled={!schemes.length || loading} size="small" sx={iconBtn}>
              <GetApp sx={{ fontSize:15 }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* ── Body ── */}
        <div style={{ overflowY:'auto', maxHeight:'calc(100vh - 100px)', padding:'6px 8px' }}>

          {!fetched && !loading && (
            <div style={{ textAlign:'center', padding:32, color:'#94a3b8', fontSize:12 }}>Select dates and refresh</div>
          )}
          {fetched && schemes.length === 0 && !loading && (
            <div style={{ textAlign:'center', padding:32, color:'#94a3b8', fontSize:12 }}>No schemes found</div>
          )}

          {fetched && schemes.length > 0 && (
            <>
              {/* ── KPI strip ── */}
              <div style={{ display:'flex', gap:6, marginBottom:8 }}>
                {[
                  { label:'Schemes', value: totalSchemes,         color:'#1d4ed8', bg:'#eff6ff' },
                  { label:'Items',   value: totalItems,           color:'#7c3aed', bg:'#f5f3ff' },
                  { label:'Sold',    value: fmtAmt(totalSoldAmt), color:'#059669', bg:'#f0fdf4' },
                  { label:'Achieved',value:`${achievedCount}/${totalSchemes}`, color:'#d97706', bg:'#fffbeb' },
                ].map(k => (
                  <div key={k.label} style={{
                    flex:1, background:k.bg, borderRadius:6,
                    padding:'5px 8px', border:`1px solid ${k.color}20`,
                  }}>
                    <div style={{ fontSize:9, color:k.color, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.3px' }}>{k.label}</div>
                    <div style={{ fontSize:15, fontWeight:800, color:k.color, lineHeight:1.3 }}>{k.value}</div>
                  </div>
                ))}
              </div>

              {/* ── Scheme cards ── */}
              {schemes.map(scheme => (
                <SchemeCard key={scheme.SchemeId} scheme={scheme} />
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SchemeSummaryReport;
